import { useEffect, useMemo } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router';
import { IconArrowLeft } from '@tabler/icons-react';

import {
  getCandidateDisplayName,
  useGetCandidate,
  useGetCandidateResume,
} from '@entities/candidates';
import {
  CandidateResume,
  CandidateSidebarInfo,
} from '@features/candidate/candidate-resume';
import { CandidateResumeActivity } from '@features/candidate/candidate-resume-activity';
import { ActionIcon, Anchor, Flex, Text, Title } from '@mantine/core';
import { useFeatureFlag } from '@packages/feature-flags/react';
import { useBreadcrumbs } from '@shared/lib';
import { EntityMetadata, WithFallback } from '@shared/ui';

import { VacancyList } from '@widgets/vacancy';
import { CandidateInterview } from '@widgets/candidate/candidate-interview';

import classes from './candidate-resume-page.module.scss';
import { getEntityMetadata } from './utils/getEntityMetadata';
import { CandidateRating } from '@widgets/candidate';

export const CandidateResumePage = () => {
  const navigate = useNavigate();
  const { id, resumeId } = useParams();
  const { data: candidate, isLoading: isLoadingCandidate } = useGetCandidate(
    id ?? ''
  );
  const { data: resume, isLoading: isLoadingCandidateResume } =
    useGetCandidateResume(id ?? '', resumeId ?? '');
  const { clearBreadcrumb, setBreadcrumb } = useBreadcrumbs();
  const ratingsAndReviewsEnabled = useFeatureFlag('ratingsAndReviews');

  useEffect(() => {
    if (!candidate || !resume) {
      return;
    }

    const candidateName = getCandidateDisplayName(candidate);
    const resumeName = resume.desiredJob?.trim() || 'Резюме';

    setBreadcrumb(
      <Flex gap={4} wrap='nowrap' align='center'>
        <Anchor to='/candidate-list' component={NavLink} variant='gray'>
          Кандидаты
        </Anchor>
        <Text>/</Text>
        <Anchor
          to={`/candidate-list/${candidate.id}`}
          component={NavLink}
          variant='gray'
        >
          {candidateName}
        </Anchor>
        <Text>/</Text>
        <Text c='tertiary.4' truncate='end' maw='40dvw'>
          {resumeName}
        </Text>
      </Flex>
    );

    return clearBreadcrumb;
  }, [candidate, clearBreadcrumb, resume, setBreadcrumb]);

  // TODO: add fallback for candidate and resume errors

  const data = useMemo(() => {
    if (!candidate || !resume) {
      return null;
    }

    return { candidate, resume };
  }, [candidate, resume]);

  return (
    <WithFallback
      data={data}
      isLoading={isLoadingCandidateResume || isLoadingCandidate}
    >
      {processedData => (
        <Flex
          wrap='wrap'
          direction='column'
          gap={32}
          mx='auto'
          maw={1512}
          w='100%'
        >
          <EntityMetadata
            items={[
              ...getEntityMetadata(processedData.resume),
              {
                id: 'activity',
                label: 'Активность:',
                value: (
                  <CandidateResumeActivity
                    candidateId={processedData.resume.candidateId}
                    resumeId={processedData.resume.resumeId}
                    isActive={processedData.resume.is_active}
                  />
                ),
              },
            ]}
          />

          <Flex align='center' gap={16} w='100%'>
            <ActionIcon
              variant='outline'
              color='primary.5'
              size={38}
              className={classes.actionIcon}
              onClick={() => navigate(-1)}
            >
              <IconArrowLeft size={20} />
            </ActionIcon>

            <Title
              order={3}
              c='primary.7'
              w='100%'
              title={processedData.resume.desiredJob || '-'}
              className={classes.title}
            >
              {processedData.resume.desiredJob || '-'}
            </Title>
          </Flex>
          <Flex
            wrap='wrap'
            gap={24}
            align='flex-start'
            direction={{ base: 'column', md: 'row' }}
          >
            <Flex direction='column' gap={24} flex={1} w='100%' maw={1070}>
              <CandidateResume data={processedData.resume} />
              <CandidateInterview resumeId={processedData.resume.resumeId} />
            </Flex>
            <Flex direction='column' gap={24} w={{ base: '100%', md: 'auto' }}>
              <CandidateSidebarInfo data={processedData.resume} />
              {ratingsAndReviewsEnabled && (
                <CandidateRating rating={processedData.candidate.rating} />
              )}
              <VacancyList
                title='Предложенные вакансии'
                vacancies={processedData.resume.vacancies.slice(0, 3)}
                totalVacancies={processedData.resume.vacancies.length}
                viewAllHref={`/candidate-list/${processedData.candidate.id}/resume/${processedData.resume.resumeId}/proposed-vacancies`}
              />
            </Flex>
          </Flex>
        </Flex>
      )}
    </WithFallback>
  );
};
